import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParametersJson, useParametersDraftJson, useSaveParameters, useSaveDraftParameters } from '@/hooks/useParameters';
import { usePermission } from '@/hooks/useUserPermissions';
import { useNotificationStore } from '@/stores/notificationStore';
import type { ParameterDefinition, ParametersFile } from '@/types/logicApps';
import { telemetryService } from '@/services/telemetryService';

export interface UseParametersEditorOptions {
  projectName: string;
  appName: string;
  appId: string;
  /**
   * When false, all data fetches are skipped and state resets. Use to gate
   * fetches behind dialog-open / app-selected.
   */
  enabled: boolean;
}

export interface ParametersEditorState {
  // Permissions
  userCanEdit: boolean;
  permissionsLoading: boolean;

  // Data fetching
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;

  // Draft mode
  isDraftMode: boolean;
  setIsDraftMode: (value: boolean) => void;
  isDraftLoading: boolean;
  isDraftError: boolean;
  refetchDraftJson: () => void;

  // JSON editor state
  rawJson: string;
  jsonError: string | null;
  hasChanges: boolean;
  isValidJson: boolean;
  /**
   * Structural error reported by the form view (duplicate / empty names) that
   * would cause a lossy save if serialized to JSON. `null` when the form is
   * clean or the JSON view is active.
   */
  formError: string | null;
  setFormError: (error: string | null) => void;
  isSaving: boolean;
  handleJsonChange: (value: string) => void;
  handleSave: () => void | Promise<void>;
  handleDiscard: () => void;
}

type ParseResult =
  | { ok: true; file: ParametersFile }
  | { ok: false; message: string };

/**
 * Parse the editor's raw JSON into the on-disk `ParametersFile` shape and
 * surface a human-readable error for any structural problem that would cause
 * a lossy save (non-object root, primitive entries, etc.). Also flattens the
 * editor's `description` field into `metadata.description` to match disk shape.
 */
const parseEditorJson = (rawJson: string): ParseResult => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    return { ok: false, message: (e as Error).message };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, message: 'Parameters must be a JSON object keyed by parameter name.' };
  }
  const entries = Object.entries(parsed as Record<string, unknown>);
  const invalidNames = entries
    .filter(([, raw]) => !raw || typeof raw !== 'object' || Array.isArray(raw))
    .map(([name]) => name);
  if (invalidNames.length > 0) {
    const list = invalidNames.map((n) => `"${n}"`).join(', ');
    return {
      ok: false,
      message: `Each parameter must be an object with at least a "type" field. Invalid: ${list}.`,
    };
  }
  const file: ParametersFile = {};
  for (const [name, raw] of entries) {
    const def = raw as Record<string, unknown> & {
      metadata?: { description?: string } | undefined;
    };
    const description =
      typeof def.description === 'string' ? def.description : def.metadata?.description;
    const next: ParameterDefinition = {
      type: String(def.type ?? 'String'),
      value: def.value,
    };
    if (description) next.metadata = { description };
    file[name] = next;
  }
  return { ok: true, file };
};

/**
 * Shared state hook for the Parameters editor. Backs both the dialog and the
 * full-page variants so data flow, permission gating, and JSON edit state
 * are identical regardless of presentation chrome.
 */
export function useParametersEditor({
  projectName,
  appName,
  appId,
  enabled,
}: UseParametersEditorOptions): ParametersEditorState {
  const { data: userCanEdit = false, isLoading: permissionsLoading } = usePermission(
    enabled ? projectName : '',
    enabled ? appName : undefined,
    ['Contributor'],
  );

  const [rawJson, setRawJson] = useState('');
  const [originalJson, setOriginalJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isDraftMode, setIsDraftMode] = useState(false);

  // Single context for every parameters query/mutation. Null disables all of
  // them in one place (no app selected, dialog closed, etc.).
  const appContext = useMemo(
    () => (enabled && appId ? { projectName, appName, appId } : null),
    [enabled, appId, projectName, appName],
  );

  const prodQuery = useParametersJson(appContext);
  const draftQuery = useParametersDraftJson(appContext);
  const activeQuery = isDraftMode ? draftQuery : prodQuery;

  const addNotification = useNotificationStore((s) => s.addNotification);
  const saveProdMutation = useSaveParameters(appContext);
  const saveDraftMutation = useSaveDraftParameters(appContext);
  const activeSaveMutation = isDraftMode ? saveDraftMutation : saveProdMutation;

  const resetEditor = useCallback(() => {
    setRawJson('');
    setOriginalJson('');
    setJsonError(null);
    setFormError(null);
  }, []);

  // Reset whenever the target app changes — without this, unsaved edits made
  // against the previous app would be preserved and could end up persisted
  // against the new app.
  const isFirstAppMount = useRef(true);
  useEffect(() => {
    if (isFirstAppMount.current) {
      isFirstAppMount.current = false;
      return;
    }
    resetEditor();
  }, [projectName, appName, appId, resetEditor]);

  // Seed (or re-seed) the editor whenever the upstream JSON for the active
  // mode changes — but only if the user has no unsaved edits.
  useEffect(() => {
    if (!enabled) {
      resetEditor();
      return;
    }
    if (activeQuery.isLoading || !activeQuery.data) return;

    const json = JSON.stringify(activeQuery.data, null, 2);
    setOriginalJson((prevOriginal) => {
      setRawJson((prevRaw) => (prevRaw === prevOriginal ? json : prevRaw));
      return json;
    });
  }, [enabled, activeQuery.data, activeQuery.isLoading, resetEditor]);

  // Force re-seed when draft mode toggles so the editor immediately reflects
  // the matching file (parameters.json ↔ parameters-draft.json).
  const prevDraftMode = useRef(isDraftMode);
  useEffect(() => {
    if (prevDraftMode.current === isDraftMode) return;
    prevDraftMode.current = isDraftMode;
    if (activeQuery.data) {
      const json = JSON.stringify(activeQuery.data, null, 2);
      setRawJson(json);
      setOriginalJson(json);
      setJsonError(null);
      setFormError(null);
    }
  }, [isDraftMode, activeQuery.data]);

  const hasChanges = rawJson !== originalJson;
  const isValidJson = jsonError === null;

  const handleJsonChange = useCallback((value: string) => {
    setRawJson(value);
    try {
      JSON.parse(value);
      setJsonError(null);
      // Explicit JSON replacement supersedes a stale form-level structural
      // error (e.g. duplicate names flagged before the user switched tabs).
      setFormError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }, []);

  const notifyInvalid = useCallback(
    (message: string) =>
      addNotification({ type: 'error', title: 'Invalid parameters', message }),
    [addNotification],
  );

  const handleSave = useCallback(async () => {
    // Guard against a lossy save when the form view has flagged duplicate or
    // empty names — a JSON object can't represent those, so the serialized
    // rawJson would silently drop entries.
    if (formError) {
      notifyInvalid(formError);
      return;
    }

    const result = parseEditorJson(rawJson);
    if (!result.ok) {
      setJsonError(result.message);
      notifyInvalid(result.message);
      return;
    }

    const fileName = isDraftMode ? 'parameters-draft.json' : 'parameters.json';
    try {
      await activeSaveMutation.mutateAsync(result.file);
      setOriginalJson(rawJson);
      addNotification({
        type: 'success',
        title: 'Parameters saved',
        message: `${fileName} has been updated.`,
      });
      telemetryService.trackEvent(
        'parameters.saved',
        { draft: String(isDraftMode) },
        { count: Object.keys(result.file).length },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save parameters';
      addNotification({ type: 'error', title: 'Save failed', message });
      telemetryService.trackException(error as Error, { context: 'parameters.save' });
    }
  }, [rawJson, formError, isDraftMode, activeSaveMutation, addNotification, notifyInvalid]);

  const handleDiscard = useCallback(() => {
    setRawJson(originalJson);
    setJsonError(null);
    setFormError(null);
  }, [originalJson]);

  return {
    userCanEdit,
    permissionsLoading,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    refetch: activeQuery.refetch,
    isDraftMode,
    setIsDraftMode,
    isDraftLoading: draftQuery.isLoading,
    isDraftError: draftQuery.isError,
    refetchDraftJson: draftQuery.refetch,
    rawJson,
    jsonError,
    hasChanges,
    isValidJson,
    formError,
    setFormError,
    isSaving: saveProdMutation.isPending || saveDraftMutation.isPending,
    handleJsonChange,
    handleSave,
    handleDiscard,
  };
}
